import { mergeOptions } from '../functions/helperFunctions';

const OPEN_CLASS = 'is-open';

class Accordion {
    // 1. describe and create/initiate our object
    constructor(accordionElement, accordionData = {}) {
        this.accordionDefaults = {
            btnClass: 'jsAccordionBtn',
            contentClass: 'jsAccordionContent',
            openAllBtnClass: 'jsAccordionOpenAllBtn',
            accordionOpenOnInit: false,
            allowOpeningAll: false,
        };

        this.accordionData = mergeOptions(
            this.accordionDefaults,
            accordionData,
        );

        this.accordion = accordionElement;
        this.accordionClosed = false;
        this.accordionActive = false;

        if (this.accordion) {
            this.accordionBtns = [
                ...this.accordion.getElementsByClassName(
                    this.accordionData.btnClass,
                ),
            ];

            this.accordionContents = [
                ...this.accordion.getElementsByClassName(
                    this.accordionData.contentClass,
                ),
            ];

            this.openAllBtn = this.accordion.getElementsByClassName(
                this.accordionData.openAllBtnClass,
            );

            if (this.accordionBtns.length && this.accordionContents.length) {
                this.events();
            }
        }
    }

    // 2. events
    events() {
        window.addEventListener(
            'DOMContentLoaded',
            this.prepareAccordion.bind(this),
            { once: true },
        );

        this.accordion.addEventListener(
            'click',
            this.handleAccordionClick.bind(this),
        );

        this.accordion.addEventListener(
            'keydown',
            this.handleKeyDown.bind(this),
        );

        if (this.openAllBtn.length) {
            this.openAllBtn[0].addEventListener(
                'click',
                this.openAllItems.bind(this),
            );
        }
    }

    // 3. methods (function, action...)
    prepareAccordion() {
        let hasOpenedItems = false;

        this.accordionBtns.forEach((btn, idx) => {
            if (this.accordionData.accordionOpenOnInit && idx === 0) {
                btn.setAttribute('aria-expanded', 'true');
            } else if (btn.hasAttribute('data-opened-onload')) {
                btn.setAttribute('aria-expanded', 'true');
                hasOpenedItems = true;
            } else {
                btn.setAttribute('aria-expanded', 'false');
            }
        });

        this.accordionContents.forEach((content, idx) => {
            if (this.accordionData.accordionOpenOnInit && idx === 0) {
                content.classList.add(OPEN_CLASS);
            } else if (content.hasAttribute('data-opened-onload')) {
                content.classList.add(OPEN_CLASS);
                hasOpenedItems = true;
            } else {
                content.classList.remove(OPEN_CLASS);
            }
        });

        if (this.accordionData.accordionOpenOnInit || hasOpenedItems) {
            this.accordionClosed = false;
        } else {
            this.accordionClosed = true;
        }

        this.accordionActive = true;
    }

    closeAllItems() {
        const btnsWithOpenedItems = this.accordionBtns.filter(
            (btn) => btn.getAttribute('aria-expanded') === 'true',
        );

        if (!btnsWithOpenedItems.length) return;

        btnsWithOpenedItems.forEach((btn) => {
            const itemData = btn.dataset.item;

            if (!itemData) return;

            const filteredItems = this.accordionContents.filter(
                (content) => itemData === content.dataset.item,
            );

            if (!filteredItems.length) return;

            const itemToOpen = filteredItems[0];

            this.closeAccordionItem(btn, itemToOpen);
        });

        this.accordionClosed = true;
    }

    openAllItems() {
        if (!this.accordionData.allowOpeningAll) return;

        const btnsWithClosedItems = this.accordionBtns.filter(
            (btn) => btn.getAttribute('aria-expanded') === 'false',
        );

        if (!btnsWithClosedItems.length) return;

        btnsWithClosedItems.forEach((btn) => {
            const itemData = btn.dataset.item;

            if (!itemData) return;

            const filteredItems = this.accordionContents.filter(
                (content) => itemData === content.dataset.item,
            );

            if (!filteredItems.length) return;

            const itemToOpen = filteredItems[0];

            this.openAccordionItem(btn, itemToOpen);
        });

        this.accordionClosed = false;
    }

    handleAccordionClick(e) {
        const clickedElement = e.target.closest(
            `.${this.accordionData.btnClass}`,
        );

        if (!clickedElement) return;

        const itemData = clickedElement.dataset.item;

        if (!itemData) return;

        const filteredItems = this.accordionContents.filter(
            (content) => itemData === content.dataset.item,
        );

        if (!filteredItems.length) return;

        const itemToOpen = filteredItems[0];

        if (!itemToOpen.classList.contains(OPEN_CLASS)) {
            if (!this.accordionData.allowOpeningAll) this.closeAllItems();

            this.openAccordionItem(clickedElement, itemToOpen);
        } else {
            if (!this.accordionData.allowOpeningAll) {
                this.closeAllItems();
            } else {
                this.closeAccordionItem(clickedElement, itemToOpen);
            }
        }
    }

    openAccordionItem(btn, item) {
        btn.setAttribute('aria-expanded', 'true');
        item.classList.add(OPEN_CLASS);
        this.accordionClosed = false;
    }

    closeAccordionItem(btn, item) {
        btn.setAttribute('aria-expanded', 'false');
        item.classList.remove(OPEN_CLASS);

        const openedItems = this.accordionContents.filter((content) =>
            content.classList.contains(OPEN_CLASS),
        );

        if (!openedItems.length) this.accordionClosed = true;
    }

    handleKeyDown(e) {
        const btn = e.target.closest(`.${this.accordionData.btnClass}`);

        if (!btn) return;

        const idx = this.accordionBtns.indexOf(btn);
        let newIdx;

        switch (e.key) {
            case 'ArrowDown':
                newIdx = (idx + 1) % this.accordionBtns.length;
                break;
            case 'ArrowUp':
                newIdx =
                    (idx - 1 + this.accordionBtns.length) %
                    this.accordionBtns.length;
                break;
            case 'Home':
                newIdx = 0;
                break;
            case 'End':
                newIdx = this.accordionBtns.length - 1;
                break;
            default:
                return;
        }

        e.preventDefault();
        this.accordionBtns[newIdx].focus();
    }
}

export default Accordion;
